// src/util/dashboardCalculations.js
// Funciones puras de cálculo financiero - SIN IA

/**
 * Calcula días de inactividad desde la última transacción
 * @param {Array} transacciones - Lista de transacciones
 * @returns {number} Días de inactividad
 */
export const calcularDiasInactividad = (transacciones) => {
  if (!transacciones || transacciones.length === 0) return 0;
  
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  
  let ultimaFecha = null;
  transacciones.forEach(t => {
    const fecha = t.fecha?.toDate ? t.fecha.toDate() : new Date(t.fecha);
    if (!ultimaFecha || fecha > ultimaFecha) {
      ultimaFecha = fecha;
    }
  });
  
  if (!ultimaFecha) return 0;
  ultimaFecha.setHours(0, 0, 0, 0);
  const diffTime = hoy - ultimaFecha;
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Calcula tendencia de ventas semana vs semana anterior
 * @param {Array} transacciones - Lista de transacciones con fecha y valor
 * @returns {Object} Porcentaje, dirección y valores
 */
export const calcularTendenciaSemanal = (transacciones) => {
  if (!transacciones || transacciones.length === 0) {
    return { 
      porcentaje: 0, 
      direccion: 'neutral',
      valorAnterior: 0,
      valorActual: 0
    };
  }
  
  const hoy = new Date();
  let totalEsta = 0;
  let totalPasada = 0;
  
  transacciones.forEach(t => {
    // Solo considerar ingresos (ventas)
    if (t.tipo !== 'ingreso') return;
    
    const fecha = t.fecha?.toDate ? t.fecha.toDate() : new Date(t.fecha);
    const diffDias = Math.floor((hoy - fecha) / (1000 * 60 * 60 * 24));
    const valor = t.valor || 0;
    
    if (diffDias <= 7) {
      totalEsta += valor;
    } else if (diffDias <= 14) {
      totalPasada += valor;
    }
  });
  
  if (totalPasada === 0) {
    return { 
      porcentaje: totalEsta > 0 ? 100 : 0, 
      direccion: totalEsta > 0 ? 'up' : 'neutral',
      valorAnterior: totalPasada,
      valorActual: totalEsta
    };
  }
  
  const porcentaje = ((totalEsta - totalPasada) / totalPasada) * 100;
  return {
    porcentaje: Math.abs(Math.round(porcentaje)),
    direccion: porcentaje >= 0 ? 'up' : 'down',
    valorAnterior: totalPasada,
    valorActual: totalEsta
  };
};

/**
 * Calcula productos estrella (mayor facturación)
 * @param {Array} transacciones - Lista de transacciones
 * @param {number} limite - Cantidad máxima de productos a retornar
 * @returns {Array} Productos estrella con nombre y total
 */
export const calcularProductosEstrella = (transacciones, limite = 5) => {
  if (!transacciones || transacciones.length === 0) return [];
  
  const productoVentas = {};
  
  transacciones.forEach(t => {
    // Solo considerar ingresos (ventas)
    if (t.tipo !== 'ingreso') return;
    
    const concepto = t.concepto || t.texto || 'Sin nombre';
    const valor = t.valor || 0;
    
    productoVentas[concepto] = (productoVentas[concepto] || 0) + valor;
  });
  
  const resultados = Object.entries(productoVentas)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limite)
    .map(([nombre, total]) => ({ nombre, total }));
  
  return resultados;
};

/**
 * Calcula productos hueso (sin rotación > umbralDías)
 * @param {Array} inventario - Lista de inventario
 * @param {Array} transacciones - Lista de transacciones
 * @param {number} umbralDias - Días sin ventas para considerar hueso
 * @returns {Array} Productos hueso
 */
export const calcularProductosHueso = (inventario, transacciones, umbralDias = 30) => {
  if (!inventario || inventario.length === 0) return [];
  if (!transacciones || transacciones.length === 0) return inventario.filter(item => item.cantidad > 0);
  
  const hoy = new Date();
  const fechasVenta = {};
  
  transacciones.forEach(t => {
    if (t.tipo !== 'ingreso') return;
    
    const concepto = t.concepto || t.texto;
    if (!concepto) return;
    
    const fecha = t.fecha?.toDate ? t.fecha.toDate() : new Date(t.fecha);
    if (!fechasVenta[concepto] || fecha > fechasVenta[concepto]) {
      fechasVenta[concepto] = fecha;
    }
  });
  
  return inventario.filter(item => {
    if (!item.producto || item.cantidad <= 0) return false;
    const ultimaVenta = fechasVenta[item.producto];
    if (!ultimaVenta) return true;
    const diffDias = Math.floor((hoy - ultimaVenta) / (1000 * 60 * 60 * 24));
    return diffDias > umbralDias;
  });
};

/**
 * Calcula margen neto
 * @param {Array} transacciones - Lista de transacciones
 * @returns {number} Margen neto porcentual
 */
export const calcularMargenNeto = (transacciones) => {
  if (!transacciones || transacciones.length === 0) return 0;
  
  let ventas = 0;
  let gastos = 0;
  
  transacciones.forEach(t => {
    const valor = t.valor || 0;
    if (t.tipo === 'ingreso') {
      ventas += valor;
    } else {
      gastos += valor;
    }
  });
  
  if (ventas === 0) return 0;
  return Math.round(((ventas - gastos) / ventas) * 100);
};

